'use client';

import type { Variants} from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import styled from 'styled-components';

import { ArrowDownSmall, ArrowUpSmall } from '@zoomit/icons';
import { Typography, cssVar, theme } from '@zoomit/zoomit-ds';

type Props = {
  collapseTitle: string;
  countOfInCollapseItems: number;
  items: React.ReactNode[];
  gap?: number;
  className?: string;
};

const FilterSplitter = (props: Props) => {
  const { collapseTitle, countOfInCollapseItems, items, gap = 16, className } = props;
  const [isCollapsed, setIsCollapsed] = useState(false);

  const transitionTime = 0.2;
  const animationVariant: Variants = {
    collapsed: {
      height: 0,
      opacity: 0,
      marginTop: -gap,
      transition: {
        opacity: {
          duration: transitionTime,
        },
        marginTop: {
          delay: transitionTime,
        },
        height: {
          delay: transitionTime,
        },
      },
    },
    expanded: {
      height: 'auto',
      opacity: 1,
      marginTop: 0,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const childAnimationVariant = {
    collapsed: {
      opacity: 0,
      y: -10,
    },
    expanded: {
      opacity: 1,
      y: 0,
    },
  };

  return (
    <Root style={{ gap: gap }} className={className}>
      {items.slice(0, countOfInCollapseItems).map((item) => item)}
      <AnimatePresence initial={false}>
        {isCollapsed && (
          <CollapseWrapper
            style={{ gap: gap }}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            variants={animationVariant}
          >
            {items.slice(countOfInCollapseItems).map((item, idx) => (
              <motion.li
                variants={childAnimationVariant}
                style={{
                  listStyle: 'none',
                }}
                key={idx + countOfInCollapseItems}
              >
                {item}
              </motion.li>
            ))}
          </CollapseWrapper>
        )}
      </AnimatePresence>
      {items.length > countOfInCollapseItems && (
        <div
          className="collapsible-btn flex cursor-pointer flex-nowrap items-center gap-2.5"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? (
            <ArrowUpSmall size="sm" fill={cssVar(theme.grey1)} />
          ) : (
            <ArrowDownSmall size="sm" fill={cssVar(theme.grey1)} />
          )}
          <Typography textColor={cssVar(theme.grey1)} variant="callout" className="fa">
            {isCollapsed ? 'بستن' : `${items.length - countOfInCollapseItems} ${collapseTitle}`}
          </Typography>
        </div>
      )}
    </Root>
  );
};

export default FilterSplitter;

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const CollapseWrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;
